import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { useCreateAccountIdentity } from '~/hooks/apis/users/useCreateAccountIdentity';
import { useDeleteAccountIdentity } from '~/hooks/apis/users/useDeleteAccountIdentity';
import { useGetAccountIdentities } from '~/hooks/apis/users/useGetAccountIdentities';
import { useGetIdentityProviders } from '~/hooks/apis/users/useGetIdentityProviders';

import type { AccountIdentity } from '~/services/UsersService';

import {
  USER_DETAILS_GRID_CONFIG,
  USER_DETAILS_GRID_PAGE_SIZE,
  compareAlphabetically,
  getErrorMessage,
  getUserDetailsGridHeight,
} from './utils';

type Props = {
  accountId?: string;
  disabled?: boolean;
  title?: string;
};

export default function ExternalIdentitiesField({ accountId, disabled = false, title = 'External Identities' }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [identifierClaimValue, setIdentifierClaimValue] = useState('');
  const [identityPendingDelete, setIdentityPendingDelete] = useState<AccountIdentity | null>(null);
  const [page, setPage] = useState(0);
  const isActionDisabled = Boolean(disabled);

  const {
    data: identityProviders = [],
    isLoading: isLoadingIdentityProviders,
    error: identityProvidersError,
  } = useGetIdentityProviders();
  const {
    data: accountIdentities = [],
    isLoading: isLoadingAccountIdentities,
    error: accountIdentitiesError,
  } = useGetAccountIdentities(accountId || '');
  const createAccountIdentityMutation = useCreateAccountIdentity(accountId || '');
  const deleteAccountIdentityMutation = useDeleteAccountIdentity(accountId || '');
  const { identityProviderById, identityProviderNameById } = useMemo(() => {
    const providerById = new Map(identityProviders.map((identityProvider) => [identityProvider.id, identityProvider]));
    const providerNameById = new Map(
      identityProviders.map((identityProvider) => [identityProvider.id, identityProvider.name]),
    );

    return {
      identityProviderById: providerById,
      identityProviderNameById: providerNameById,
    };
  }, [identityProviders]);
  const addableIdentityProviders = useMemo(
    () => identityProviders.filter((identityProvider) => !identityProvider.auth_settings),
    [identityProviders],
  );
  const selectedIdentityProvider = useMemo(
    () => identityProviderById.get(selectedProviderId),
    [identityProviderById, selectedProviderId],
  );
  const sortedAccountIdentities = useMemo(() => {
    return [...accountIdentities].sort((leftIdentity, rightIdentity) =>
      compareAlphabetically(
        identityProviderNameById.get(leftIdentity.identity_provider_id) || leftIdentity.identity_provider_id,
        identityProviderNameById.get(rightIdentity.identity_provider_id) || rightIdentity.identity_provider_id,
      ),
    );
  }, [accountIdentities, identityProviderNameById]);
  const rows = useMemo(
    () =>
      sortedAccountIdentities.map((identity) => ({
        ...identity,
        identifierClaimName: identityProviderById.get(identity.identity_provider_id)?.identifier_claim_name || '-',
        providerName: identityProviderNameById.get(identity.identity_provider_id) || identity.identity_provider_id,
      })),
    [identityProviderById, identityProviderNameById, sortedAccountIdentities],
  );
  const gridHeight = useMemo(() => getUserDetailsGridHeight(rows.length), [rows.length]);
  const columns = useMemo<Array<GridColDef>>(
    () => [
      {
        field: 'providerName',
        headerName: 'Identity Provider',
        flex: 1,
        display: 'flex',
      },
      {
        field: 'identifierClaimName',
        headerName: 'Identifier Claim',
        flex: 1,
        display: 'flex',
      },
      {
        field: 'identifier_claim_value',
        headerName: 'Value',
        flex: 1,
        display: 'flex',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        align: 'right',
        headerAlign: 'right',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 140,
        renderCell: ({ row }) => (
          <Button
            variant="text"
            color="error"
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            onClick={() => setIdentityPendingDelete(row)}
            disabled={isActionDisabled || deleteAccountIdentityMutation.isPending}
            sx={{ mt: -1 }}
          >
            Delete
          </Button>
        ),
      },
    ],
    [deleteAccountIdentityMutation.isPending, isActionDisabled],
  );

  const resetCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setSelectedProviderId('');
    setIdentifierClaimValue('');
  };

  const handleOpenCreateDialog = () => {
    setSelectedProviderId(addableIdentityProviders[0]?.id ?? '');
    setIdentifierClaimValue('');
    setIsCreateDialogOpen(true);
  };

  const handleCreateIdentity = async () => {
    if (!selectedProviderId || !identifierClaimValue.trim()) {
      enqueueSnackbar('Identity provider and identifier claim value are required', { variant: 'error' });
      return;
    }

    try {
      await createAccountIdentityMutation.mutateAsync({
        identity_provider_id: selectedProviderId,
        identifier_claim_value: identifierClaimValue.trim(),
      });
      resetCreateDialog();
      enqueueSnackbar('External identity added', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Unable to add external identity: ${getErrorMessage(error)}`, { variant: 'error' });
    }
  };

  const handleDeleteIdentity = async () => {
    if (!identityPendingDelete) {
      return;
    }

    try {
      await deleteAccountIdentityMutation.mutateAsync(identityPendingDelete.id);
      setIdentityPendingDelete(null);
      enqueueSnackbar('External identity deleted', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Unable to delete external identity: ${getErrorMessage(error)}`, { variant: 'error' });
    }
  };

  if (!accountId) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        External identities are unavailable because the account ID is missing.
      </Alert>
    );
  }

  return (
    <Box sx={{ pb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
        <Button
          variant="contained"
          onClick={handleOpenCreateDialog}
          disabled={isActionDisabled || !addableIdentityProviders.length}
        >
          Add identity
        </Button>
      </Stack>

      {identityProvidersError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Unable to load identity providers: {getErrorMessage(identityProvidersError)}
        </Alert>
      )}

      {accountIdentitiesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Unable to load external identities: {getErrorMessage(accountIdentitiesError)}
        </Alert>
      )}

      {(isLoadingIdentityProviders || isLoadingAccountIdentities) && (
        <Box sx={{ py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!isLoadingIdentityProviders && !addableIdentityProviders.length && !identityProvidersError && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No identity providers are available to add.
        </Alert>
      )}

      {!isLoadingAccountIdentities && !accountIdentities.length && !accountIdentitiesError && (
        <Typography variant="body2" color="text.secondary">
          No external identities have been linked to this account.
        </Typography>
      )}

      {accountIdentities.length > 0 && (
        <Box sx={{ height: gridHeight }}>
          <DataGrid
            rows={rows}
            columns={columns}
            {...USER_DETAILS_GRID_CONFIG}
            initialState={{
              pagination: { paginationModel: { pageSize: USER_DETAILS_GRID_PAGE_SIZE } },
            }}
            pageSizeOptions={[USER_DETAILS_GRID_PAGE_SIZE]}
            paginationModel={{ pageSize: USER_DETAILS_GRID_PAGE_SIZE, page }}
            onPaginationModelChange={({ page }) => setPage(page)}
            disableColumnMenu
            disableRowSelectionOnClick
            slots={{ pagination: GridPagination }}
          />
        </Box>
      )}

      <Dialog
        open={isCreateDialogOpen}
        onClose={createAccountIdentityMutation.isPending ? undefined : resetCreateDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add External Identity</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Identity Provider"
              value={selectedProviderId}
              onChange={(event) => setSelectedProviderId(event.target.value)}
              disabled={createAccountIdentityMutation.isPending}
              fullWidth
              required
            >
              {addableIdentityProviders.map((identityProvider) => (
                <MenuItem key={identityProvider.id} value={identityProvider.id}>
                  {identityProvider.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={selectedIdentityProvider?.identifier_claim_name || 'Identifier Claim Value'}
              value={identifierClaimValue}
              onChange={(event) => setIdentifierClaimValue(event.target.value)}
              disabled={createAccountIdentityMutation.isPending}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetCreateDialog} disabled={createAccountIdentityMutation.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateIdentity} disabled={createAccountIdentityMutation.isPending}>
            Add identity
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(identityPendingDelete)}
        onClose={deleteAccountIdentityMutation.isPending ? undefined : () => setIdentityPendingDelete(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete External Identity</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Remove this external identity from the account?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIdentityPendingDelete(null)} disabled={deleteAccountIdentityMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteIdentity}
            disabled={deleteAccountIdentityMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
